import { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import type { FlightsClassTypes } from 'constants/flight'
import Caption from 'LuxKit/Typography/Caption'
import FlightsLoyaltyContext from 'contexts/Flights/FlightsLoyaltyContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { getFlightLoyaltyProductType } from 'lib/flights/flightUtils'
import { last, nonNullable } from 'lib/array/arrayUtils'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import { getFlightSegmentsState } from 'selectors/flightsSelectors'
import { getPointsCalculationsByKeys } from 'loyalty/selectors/pointsCalculation'
import getLoyaltyEarnKey from 'loyalty/lib/pointsCalculation/earnKey/getLoyaltyEarnKey'
import isLoyaltyCalculationRequestWithBonus from 'loyalty/lib/pointsCalculation/isLoyaltyCalculationRequestWithBonus'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import useCharterDateFareFlightFareFamilies from 'hooks/CharterFlights/useCharterDateFareFlightFareFamilies'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import MaldivesCharterFareFeatureDetailsSkeleton from './MaldivesCharterFareFeatureDetailsSkeleton'

const CellPad = styled.div`
  padding: ${rem(8)} ${rem(12)};
`

const LoyaltyEarnWrap = styled.div`
  text-align: start;
  display: block;
`

interface Props {
  fareClass: FlightsClassTypes
  checkIn: string
  checkOut: string
  rooms: Array<App.Occupants>
  /** Reward cabins pay flights with points and earn no loyalty on the flight portion — the cell
   *  skips the flight-fare earn calculation entirely so it doesn't display a misleading earn
   *  amount on top of points payment. */
  isReward: boolean
  /** Hotel-portion earn calculation request, sourced from `bundleData.hotelLoyaltyCalculationRequest`
   *  in `useCalendarItemData`. Always rendered when present — this is the only earn line for
   *  reward cabins, and stacks alongside the flight earn line for cash cabins. */
  hotelLoyaltyCalculationRequest?: App.LoyaltyPointsEarnCalculationRequest
}

function getPriceValue(price: number) {
  return Math.round(price) === 0 ? 0 : price
}

function MaldivesCharterTableExpandedSocieteCell(props: Props) {
  const { fareClass, checkIn, checkOut, rooms, isReward, hotelLoyaltyCalculationRequest } = props

  const intl = useIntl()
  const showLoyaltyPoints = useContext(FlightsLoyaltyContext)

  const {
    departingFareFamily,
    fetchingFareFeatures,
    fareFeaturesError,
    fareFeaturesFetchSuccess,
    fareFeaturesFetchEnabled,
    departingJourney,
  } = useCharterDateFareFlightFareFamilies({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    checkIn,
    checkOut,
    occupancies: rooms,
    fareClass,
  })

  const flightSegmentsState = useAppSelector(getFlightSegmentsState)

  const allLoyaltyPointsKeys = useMemo<Array<string>>(() => {
    return flightSegmentsState.segmentsData.flatMap(data => data.loyaltyPointsKeys ?? [])
  }, [flightSegmentsState.segmentsData])

  const loyaltyPointsCalculationsByKey = useAppSelector(state =>
    getPointsCalculationsByKeys(state, allLoyaltyPointsKeys),
  )

  const previouslySelectedPointsCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(
    () => loyaltyPointsCalculationsByKey.map(entry => entry?.data?.request),
    [loyaltyPointsCalculationsByKey],
  )

  const fallbackEarnKey = useMemo<string | undefined>(() => {
    const hasPreviousEarnKey = previouslySelectedPointsCalculationRequests.some(request => request?.earnKey)
    if (hasPreviousEarnKey) {
      return undefined
    }

    const earnKeys = nonNullable(
      previouslySelectedPointsCalculationRequests.map(request => {
        if (request && isLoyaltyCalculationRequestWithBonus(request) && request.earnKey) {
          return request.earnKey
        }
      }),
    )

    return earnKeys[0]
  }, [previouslySelectedPointsCalculationRequests])

  const calculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    const requests: Array<App.LoyaltyPointsEarnCalculationRequest | undefined> = []

    // Hotel-portion earn always renders when available — for cash cabins it stacks alongside the
    // flight earn line below; for reward cabins it's the only earn line (the flight is paid in
    // points and earns no loyalty).
    if (hotelLoyaltyCalculationRequest) {
      requests.push(hotelLoyaltyCalculationRequest)
    }

    if (isReward) {
      return requests
    }
    if (!departingFareFamily || !departingJourney) {
      return requests
    }
    const flights = departingJourney.flightGroup.flights
    const firstFlight = flights[0]
    const lastFlight = last(flights)
    if (!firstFlight?.departureAirport || !lastFlight?.arrivalAirport) {
      return requests
    }
    const type = getFlightLoyaltyProductType(departingFareFamily.cabin.toLowerCase(), false)
    const earnKey =
      fallbackEarnKey
      || getLoyaltyEarnKey({
        loyaltyProductType: type,
        options: {
          originAirportCode: firstFlight.departureAirport,
          destinationAirportCode: lastFlight.arrivalAirport,
          carrier: departingJourney.carrier,
          loyaltyEarnKey: departingFareFamily.loyaltyEarnKey,
        },
      })

    requests.push({
      loyaltyProductType: type,
      price: getPriceValue(departingFareFamily.costWithInsurance?.all || departingFareFamily.cost.all),
      earnKey,
      offerId: departingJourney.id,
      packageId: '',
    })
    return requests
  }, [departingFareFamily, departingJourney, fallbackEarnKey, isReward, hotelLoyaltyCalculationRequest])

  const hasEarnCalculationRequests = nonNullable(calculationRequests).length > 0

  const fareDetailsLoadErrorLabel = intl.formatMessage({
    id: 'EeLZd4RtpChRnnVKPYXE',
    defaultMessage: 'We couldn’t load fare inclusions. Try again in a moment.',
    description:
      'Inline error message under the Maldives charter calendar fare card when the fare upsell request for baggage and cabin rows fails so the customer knows the comparison block did not load.',
  })

  const fareDetailsUnavailableLabel = intl.formatMessage({
    id: 'z9WF1hsm8DsldyNCbyOQ',
    defaultMessage: 'Fare inclusions will appear once matching flights are available for this stay.',
    description:
      'Helper copy in the expanded Maldives charter fare card when flight search results are not yet matched to the selected check-in and check-out dates so baggage and cabin rows cannot load yet.',
  })

  const societeUnavailableLabel = intl.formatMessage({
    id: '5if3MpGoeFgrGJ_k120j',
    defaultMessage: '{loyaltyProgramName} earn details will appear once a matching offer is available for this cabin.',
    description:
      'Muted helper sentence inside a single cabin column of the expanded Maldives charter desktop trip-options table on the Société benefits row when earn cannot be shown because the hotel package is not yet resolved.',
  }, { loyaltyProgramName: getLoyaltyProgramDisplayName() })

  const showFareDetailsSkeleton = !fareFeaturesError
    && !departingFareFamily
    && (fetchingFareFeatures || (fareFeaturesFetchEnabled && !fareFeaturesFetchSuccess))

  const showFareDetailsUnavailable = !fareFeaturesError
    && !fareFeaturesFetchEnabled
    && !departingFareFamily
    && !fetchingFareFeatures

  // Only show earn when the hotel-portion calculation is available — without it the number
  // is incomplete (cash columns would show flight-only earn, reward columns nothing).
  const showLoyaltyEarn = showLoyaltyPoints && hasEarnCalculationRequests && !!hotelLoyaltyCalculationRequest

  if (isReward) {
    return (
      <CellPad>
        {showLoyaltyEarn && (
          <LoyaltyEarnWrap>
            <LoyaltyPointsEarn calculationRequests={calculationRequests} calculationType="exact" size="S" />
          </LoyaltyEarnWrap>
        )}
        {!showLoyaltyEarn && (
          <Caption variant="medium" colour="neutral-three">
            {societeUnavailableLabel}
          </Caption>
        )}
      </CellPad>
    )
  }

  return (
    <CellPad>
      {fareFeaturesError && (
        <Caption variant="medium" colour="critical">
          {fareDetailsLoadErrorLabel}
        </Caption>
      )}
      {showFareDetailsSkeleton && <MaldivesCharterFareFeatureDetailsSkeleton />}
      {showFareDetailsUnavailable && (
        <Caption variant="medium" colour="neutral-three">
          {fareDetailsUnavailableLabel}
        </Caption>
      )}
      {!fareFeaturesError
        && !showFareDetailsSkeleton
        && !showFareDetailsUnavailable
        && departingFareFamily
        && !departingJourney && (
        <Caption variant="medium" colour="neutral-three">
          {societeUnavailableLabel}
        </Caption>
      )}
      {!fareFeaturesError
        && !showFareDetailsSkeleton
        && !showFareDetailsUnavailable
        && departingFareFamily
        && departingJourney
        && showLoyaltyEarn && (
        <LoyaltyEarnWrap>
          <LoyaltyPointsEarn calculationRequests={calculationRequests} calculationType="exact" size="S" />
        </LoyaltyEarnWrap>
      )}
      {!fareFeaturesError
        && !showFareDetailsSkeleton
        && !showFareDetailsUnavailable
        && departingFareFamily
        && departingJourney
        && showLoyaltyPoints
        && !showLoyaltyEarn && (
        <Caption variant="medium" colour="neutral-three">
          {societeUnavailableLabel}
        </Caption>
      )}
    </CellPad>
  )
}

export default MaldivesCharterTableExpandedSocieteCell
